<template>
  <div class="rounded-lg bg-gray-50 p-4">
    <div v-if="timezone" class="flex flex-row items-center gap-2 border-b border-gray-100 py-3">
      <Globe05Icon class="h-5 w-5 text-gray-500" />
      {{
        computedTimezones
          .map(x => x.children)
          .flat()
          .find(x => x.value === timezone)?.label
      }}
    </div>
    <div
      v-for="(day, index) in schedule.getWeeklySchedule()"
      :key="index"
      class="flex flex-row items-start border-b border-gray-100 py-3"
    >
      <div class="w-16 pt-1 font-medium capitalize text-gray-700">
        {{ capitalizeAbbr(day.day || '') }}
      </div>
      <div class="flex flex-col items-center gap-2">
        <template v-if="day.isActive && day.intervals.length">
          <span
            v-for="(slot, i) in day.intervals"
            :key="i"
            class="py-1 text-sm"
          >
            {{ formatTime(slot.from) }} - {{ formatTime(slot.to) }}
          </span>
        </template>
        <span v-else class="mt-1 text-sm text-gray-400">{{
          t('settings.availability.schedule.unavailable')
        }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computedTimezones } from '@/utils/timezonesOptions'

import { Globe05Icon } from '@gohighlevel/ghl-icons/24/outline'

import { computed, PropType, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { Schedule } from '../Schedule/Schedule'

const props = defineProps({
  schedule: { type: Object as PropType<Schedule>, required: true },
  capitalizeAbbr: { type: Function, required: true },
})

const schedule = ref(props.schedule)

watch(
  () => props.schedule,
  value => {
    schedule.value = value
  }
)

const timezone = computed(() => schedule.value.timezone)

const { t } = useI18n()

function formatTime(time: string) {
  // Expects 'H:m' or 'HH:mm', returns 'hh:mm AM/PM'
  if (!time) {
    return ''
  }
  const [h, m] = time.split(':').map(Number)
  if (isNaN(h) || isNaN(m)) {
    return time
  }
  const date = new Date()
  date.setHours(h)
  date.setMinutes(m)
  return date
    .toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase()
}
</script>
